/**
 * Task Management Types
 *
 * Short alphanumeric IDs (5 chars) for voice-friendly identification.
 * Example: "C6A23" = "Charlie Six Alpha Two Three"
 */

// =============================================================================
// Enums
// =============================================================================

/**
 * Task status - now COMPUTED from iteration statuses.
 *
 * The status is derived from the last non-draft iteration:
 * - 'open': No non-draft iterations, or last was cancelled
 * - 'in_progress': Last iteration is pending/dispatching/processing
 * - 'blocked': Last iteration failed
 * - 'completed': Last iteration completed
 * - 'archived': Task archived flag is set
 *
 * Legacy values (pending, ready, cancelled) are kept for backward compatibility.
 */
export type TaskStatus = 'draft' | 'open' | 'pending' | 'ready' | 'in_progress' | 'waiting' | 'for_review' | 'blocked' | 'completed' | 'cancelled' | 'archived';
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';
export type TaskActor = 'user' | 'ai';  // Legacy - use AgentType instead
export type AgentType = 'self' | 'ai';  // New unified agent type
export type DependencyType = 'blocks' | 'requires' | 'related';

// =============================================================================
// Agent Types (Unified Assignee Model)
// =============================================================================

/**
 * Agent that can be assigned to tasks.
 * Includes both self-agents (user themselves) and AI agents.
 */
export interface AgentAssignee {
  agent_id: number;
  agent_name: string;
  agent_type: AgentType;
  description: string | null;
  is_self_agent: boolean;
  priority: number;
}

export const AGENT_TYPE_CONFIG: Record<AgentType, {
  icon: string;
  color: string;
  bgColor: string;
}> = {
  self: {
    icon: '👤',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
  },
  ai: {
    icon: '🤖',
    color: 'text-purple-700',
    bgColor: 'bg-purple-100',
  },
};

// Status display configuration
// NOTE: Task status is now COMPUTED from iteration statuses
export const TASK_STATUS_CONFIG: Record<TaskStatus, {
  color: string;
  bgColor: string;
  borderColor: string;
  icon: string;
}> = {
  draft: {
    color: 'text-slate-600',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-200',
    icon: '📝',
  },
  open: {
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    icon: '📋',
  },
  pending: {
    color: 'text-gray-700',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    icon: '⏳',
  },
  ready: {
    color: 'text-indigo-700',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    icon: '👁',
  },
  in_progress: {
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    icon: '🔄',
  },
  waiting: {
    color: 'text-orange-700',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    icon: '⏸',
  },
  for_review: {
    color: 'text-indigo-700',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    icon: '👁',
  },
  blocked: {
    color: 'text-red-700',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    icon: '🚫',
  },
  completed: {
    color: 'text-green-700',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    icon: '✓',
  },
  cancelled: {
    color: 'text-gray-500',
    bgColor: 'bg-gray-100',
    borderColor: 'border-gray-300',
    icon: '✗',
  },
  archived: {
    color: 'text-gray-400',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    icon: '📦',
  },
};

// Manual statuses available for non-AI task dropdown
export const MANUAL_TASK_STATUSES: TaskStatus[] = [
  'draft', 'open', 'pending', 'in_progress', 'blocked', 'waiting', 'for_review', 'completed', 'cancelled',
];

export const TASK_PRIORITY_CONFIG: Record<TaskPriority, {
  color: string;
  bgColor: string;
  dotColor: string;
}> = {
  low: {
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
    dotColor: 'bg-gray-400',
  },
  medium: {
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    dotColor: 'bg-blue-500',
  },
  high: {
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
    dotColor: 'bg-orange-500',
  },
  urgent: {
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    dotColor: 'bg-red-600',
  },
};

export const TASK_ACTOR_CONFIG: Record<TaskActor, {
  icon: string;
  color: string;
  bgColor: string;
}> = {
  user: {
    icon: '👤',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
  },
  ai: {
    icon: '🤖',
    color: 'text-purple-700',
    bgColor: 'bg-purple-100',
  },
};

// Kanban column order - all non-archived statuses in preferred order
export const KANBAN_COLUMN_ORDER: TaskStatus[] = [
  'draft',
  'open',
  'pending',
  'in_progress',
  'waiting',
  'for_review',
  'blocked',
  'completed',
  'cancelled',
  // 'archived' intentionally excluded - archived tasks hidden from board
  // 'ready' excluded - deprecated, replaced by 'for_review'
];

// Core columns always shown (for drag-drop targets even when empty)
export const KANBAN_CORE_COLUMNS: TaskStatus[] = [
  'open',
  'in_progress',
  'completed',
];

// Legacy: hardcoded columns (deprecated - use getActiveKanbanColumns instead)
export const KANBAN_COLUMNS: TaskStatus[] = KANBAN_COLUMN_ORDER;

/**
 * Get active Kanban columns based on tasks.
 * Shows core columns + any other columns that have tasks.
 */
export function getActiveKanbanColumns(tasksByStatus: Record<TaskStatus, Task[]>): TaskStatus[] {
  const activeSet = new Set<TaskStatus>(KANBAN_CORE_COLUMNS);

  // Add any status that has tasks
  for (const status of KANBAN_COLUMN_ORDER) {
    if (tasksByStatus[status]?.length > 0) {
      activeSet.add(status);
    }
  }

  // Return in preferred order
  return KANBAN_COLUMN_ORDER.filter(status => activeSet.has(status));
}

// =============================================================================
// Core Types
// =============================================================================

export interface Task {
  id: string;  // 5-char alphanumeric, e.g., "C6A23"
  title: string;
  description: string | null;

  created_by: TaskActor;
  assigned_to: TaskActor | null;  // Legacy - use assigned_to_agent_id instead

  // New unified agent assignment
  assigned_to_agent_id: number | null;
  assigned_agent_name: string | null;  // Display name (e.g., "Me" for self-agent)
  assigned_agent_type: AgentType | null;  // "self" or "ai"

  status: TaskStatus;
  priority: TaskPriority;

  user_id: number;

  due_date: string | null;
  estimated_minutes: number | null;
  actual_minutes: number | null;
  started_at: string | null;
  completed_at: string | null;

  sort_order: number;

  position_x: number;
  position_y: number;

  tags: string[];
  metadata: Record<string, unknown>;

  archived: boolean;
  archived_at: string | null;

  has_unread_iterations: boolean;

  created_at: string;
  updated_at: string;

  // Current iteration info (task IS iteration #1)
  current_iteration_id?: string | null;
  current_iteration_number?: number | null;
  current_iteration_status?: IterationStatus | null;

  // Populated fields
  dependencies?: TaskDependency[];
  dependents?: TaskDependency[];
  is_blocked?: boolean;
  blocking_tasks?: string[];

  // Institutional features
  assigned_to_user_id: number | null;
  assigned_user_full_name: string | null;
  assigned_user_avatar_url: string | null;
  project_id: string | null;
  project_name: string | null;
  project_color: string | null;
  due_time: string | null;  // "HH:MM" format
  report_text: string | null;
}

export interface TaskDependency {
  id: string;
  task_id: string;
  depends_on_task_id: string;
  dependency_type: DependencyType;
  user_id: number;
  created_at: string;

  // Populated for display
  depends_on_title?: string;
  depends_on_status?: TaskStatus;
  title?: string;
  status?: TaskStatus;
}

export interface TaskHistory {
  id: number;
  task_id: string;
  user_id: number;
  username: string | null;
  full_name: string | null;
  avatar_url: string | null;
  action: string;
  field_changed: string | null;
  old_value: unknown;
  new_value: unknown;
  changed_by: TaskActor;
  created_at: string;
}

export interface TaskStats {
  total: number;
  by_status: Record<string, number>;
  by_assignee: Record<string, number>;
  by_priority: Record<string, number>;
  blocked: number;
  overdue: number;
  archived: number;
}

export interface ArchiveStats {
  total_archived: number;
  by_status: Record<string, number>;
  recent_archived_7d: number;
  oldest_archived_at: string | null;
}

export interface BulkArchiveResult {
  archived: string[];
  archived_count: number;
  failed: Array<{ id: string; reason: string }>;
  failed_count: number;
}

export interface AutoArchiveResult {
  completed_archived: number;
  cancelled_archived: number;
  total_archived: number;
  completed_cutoff_days: number;
  cancelled_cutoff_days: number;
}

// =============================================================================
// Attachment Types
// =============================================================================

export interface TaskAttachment {
  id: string;  // 8-char alphanumeric
  task_id: string;
  user_id: number;
  file_name: string;
  file_size: number;
  content_type: string | null;
  s3_key: string;
  uploaded_at: string;
  iteration_id?: string | null;  // If set, attachment belongs to an iteration
  artifact_type?: ArtifactType;  // Type of attachment
}

export interface AttachmentUploadResponse {
  attachment: TaskAttachment;
  message: string;
}

export interface AttachmentDownloadResponse {
  url: string;
  file_name: string;
  expires_in: number;
}

// =============================================================================
// Iteration Types
// =============================================================================

/**
 * Iteration status - the unit of work.
 *
 * Flow: draft -> pending -> dispatching -> processing -> completed/failed
 */
export type IterationStatus = 'draft' | 'pending' | 'dispatching' | 'processing' | 'completed' | 'failed' | 'cancelled';
// =============================================================================
// Institutional Types
// =============================================================================

export interface TaskProject {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  color: string;
  icon: string;
  task_count: number;
  archived: boolean;
  created_by: number;
  created_at: string;
  updated_at: string;
}

export interface AssignableUser {
  user_id: number;
  username: string;
  full_name: string;
  avatar_url: string | null;
}

export type TaskChatType = 'author_notes' | 'discussion';

export type ArtifactType = 'input' | 'output' | 'reference' | 'report';
export type IterationCreatedBy = 'user' | 'ai';

export const ITERATION_STATUS_CONFIG: Record<IterationStatus, {
  color: string;
  bgColor: string;
  borderColor: string;
  icon: string;
}> = {
  draft: {
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    icon: '📝',
  },
  pending: {
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    icon: '⏳',
  },
  dispatching: {
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    icon: '🚀',
  },
  processing: {
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    icon: '⚙️',
  },
  completed: {
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    icon: '✓',
  },
  failed: {
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    icon: '❌',
  },
  cancelled: {
    color: 'text-gray-500',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    icon: '✗',
  },
};

export const ARTIFACT_TYPE_CONFIG: Record<ArtifactType, {
  icon: string;
  color: string;
}> = {
  input: {
    icon: '📥',
    color: 'text-blue-600',
  },
  output: {
    icon: '📤',
    color: 'text-green-600',
  },
  reference: {
    icon: '📎',
    color: 'text-gray-600',
  },
  report: {
    icon: '📝',
    color: 'text-purple-600',
  },
};

export interface IterationAttachment extends TaskAttachment {
  iteration_id: string | null;
  artifact_type: ArtifactType;
}

export interface TaskIteration {
  id: string;  // 8-char alphanumeric
  task_id: string;
  iteration_number: number;
  user_prompt: string | null;
  report: string | null;
  report_created_by: IterationCreatedBy | null;
  status: IterationStatus;
  created_by: IterationCreatedBy;
  user_id: number;
  created_at: string;
  submitted_at: string | null;
  completed_at: string | null;
  rating_stars: number | null;
  rating_feedback: string | null;
  rated_at: string | null;
  attachments?: IterationAttachment[];
}

export interface IterationListResponse {
  iterations: TaskIteration[];
  total: number;
  has_unread: boolean;
}

export interface CreateIterationRequest {
  user_prompt?: string;
}

export interface UpdateIterationRequest {
  user_prompt?: string;
  report?: string;
}

export interface CompleteIterationRequest {
  report: string;
  report_created_by?: IterationCreatedBy;
}

export interface IterationAttachmentUploadResponse {
  attachment: IterationAttachment;
  message: string;
}

// =============================================================================
// Request Types
// =============================================================================

export interface CreateTaskRequest {
  title: string;
  description?: string;
  created_by?: TaskActor;
  assigned_to?: TaskActor;  // Legacy - use assigned_to_agent_id instead
  assigned_to_agent_id?: number;  // NEW: Agent-based assignment
  priority?: TaskPriority;
  due_date?: string;
  estimated_minutes?: number;
  tags?: string[];
  metadata?: Record<string, unknown>;
  position_x?: number;
  position_y?: number;
  depends_on?: string[];
  // Institutional features
  assigned_to_user_id?: number;
  project_id?: string | null;
  due_time?: string | null;
}

export interface UpdateTaskRequest {
  title?: string;
  description?: string;
  assigned_to?: TaskActor;  // Legacy - use assigned_to_agent_id instead
  assigned_to_agent_id?: number;  // NEW: Agent-based assignment
  user_prompt?: string;  // Instructions for AI when reassigning task
  status?: TaskStatus;
  priority?: TaskPriority;
  due_date?: string;
  estimated_minutes?: number;
  actual_minutes?: number;
  tags?: string[];
  metadata?: Record<string, unknown>;
  position_x?: number;
  position_y?: number;
  // Institutional features
  assigned_to_user_id?: number;
  project_id?: string | null;
  due_time?: string | null;
  report_text?: string;
}

export interface ReorderTaskRequest {
  new_status: TaskStatus;
  new_position: number;
}

export interface AddDependencyRequest {
  depends_on_task_id: string;
  dependency_type?: DependencyType;
}

export interface UpdatePositionsRequest {
  positions: Array<{
    id: string;
    x: number;
    y: number;
  }>;
}

export interface BulkCreateRequest {
  tasks: CreateTaskRequest[];
}

export interface BulkStatusUpdateRequest {
  task_ids: string[];
  status: TaskStatus;
}

export interface TasksFilter {
  status?: TaskStatus | TaskStatus[];
  assigned_to?: TaskActor;
  created_by?: TaskActor;
  priority?: TaskPriority | TaskPriority[];
  search?: string;
  due_before?: string;
  due_after?: string;
  include_dependencies?: boolean;
  include_archived?: boolean;
  archived_only?: boolean;
  project_id?: string;
}

// =============================================================================
// Graph Types (for React Flow)
// =============================================================================

export interface TaskGraphResponse {
  tasks: Task[];
  dependencies: TaskDependency[];
  nodes: TaskGraphNode[];
  edges: TaskGraphEdge[];
}

export interface TaskGraphNode {
  id: string;
  type: 'task';
  position: { x: number; y: number };
  data: { task: Task };
}

export interface TaskGraphEdge {
  id: string;
  source: string;
  target: string;
  type: string;
  animated: boolean;
  label: string;
  data: { dependency: TaskDependency };
}

// =============================================================================
// View Types
// =============================================================================

export type TaskViewMode = 'list' | 'board' | 'graph' | 'timeline';

export interface TaskColumn {
  status: TaskStatus;
  tasks: Task[];
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Format task ID for voice reading using NATO phonetic alphabet.
 * Example: "C6A23" -> "Charlie Six Alpha Two Three"
 */
export const NATO_ALPHABET: Record<string, string> = {
  'A': 'Alpha', 'B': 'Bravo', 'C': 'Charlie', 'D': 'Delta', 'E': 'Echo',
  'F': 'Foxtrot', 'G': 'Golf', 'H': 'Hotel', 'I': 'India', 'J': 'Juliet',
  'K': 'Kilo', 'L': 'Lima', 'M': 'Mike', 'N': 'November', 'O': 'Oscar',
  'P': 'Papa', 'Q': 'Quebec', 'R': 'Romeo', 'S': 'Sierra', 'T': 'Tango',
  'U': 'Uniform', 'V': 'Victor', 'W': 'Whiskey', 'X': 'X-ray', 'Y': 'Yankee',
  'Z': 'Zulu',
  '0': 'Zero', '1': 'One', '2': 'Two', '3': 'Three', '4': 'Four',
  '5': 'Five', '6': 'Six', '7': 'Seven', '8': 'Eight', '9': 'Nine',
};

export function taskIdToNato(taskId: string | null | undefined): string {
  if (!taskId) return '';
  return taskId
    .toUpperCase()
    .split('')
    .map(char => NATO_ALPHABET[char] || char)
    .join(' ');
}

/**
 * Check if a task is overdue.
 */
export function isTaskOverdue(task: Task | null | undefined): boolean {
  if (!task || !task.due_date) return false;
  if (task.status === 'completed' || task.status === 'cancelled') return false;

  const dueDate = new Date(task.due_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return dueDate < today;
}

/**
 * Check if a task is due today.
 */
export function isTaskDueToday(task: Task | null | undefined): boolean {
  if (!task || !task.due_date) return false;

  const dueDate = new Date(task.due_date);
  const today = new Date();

  return (
    dueDate.getFullYear() === today.getFullYear() &&
    dueDate.getMonth() === today.getMonth() &&
    dueDate.getDate() === today.getDate()
  );
}

/**
 * Format minutes as human-readable duration.
 */
export function formatDuration(minutes: number | null): string {
  if (!minutes) return '';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

/**
 * Group tasks by status for Kanban board.
 * Uses computed status values.
 */
export function groupTasksByStatus(tasks: Task[]): Record<TaskStatus, Task[]> {
  const grouped: Record<TaskStatus, Task[]> = {
    draft: [],
    open: [],
    pending: [],
    ready: [],
    in_progress: [],
    waiting: [],
    for_review: [],
    blocked: [],
    completed: [],
    cancelled: [],
    archived: [],
  };

  for (const task of tasks) {
    if (!task) continue;
    const status = task.status || 'open';
    if (grouped[status]) {
      grouped[status].push(task);
    } else {
      // Unknown status, default to open
      grouped.open.push(task);
    }
  }

  // Sort each column by sort_order
  for (const status of Object.keys(grouped) as TaskStatus[]) {
    grouped[status].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  }

  return grouped;
}

/**
 * Format file size in human-readable format.
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = bytes / Math.pow(k, i);
  return `${size.toFixed(i > 0 ? 1 : 0)} ${sizes[i]}`;
}

// =============================================================================
// Progress Types (for Fargate Worker real-time updates)
// =============================================================================

export interface ProgressTask {
  content: string;
  completed: boolean;
}

export interface IterationProgress {
  iteration_id: string;
  task_id: string;
  status: string;
  progress_percent: number;
  progress_message: string | null;
  progress_eta: string | null;
  progress_task_list: ProgressTask[];
  progress_status_description: string | null;
  started_at: string | null;  // ISO timestamp from server - use for timer initialization
  elapsed_seconds: number | null;  // Server-calculated, use started_at for local ticking
  agent_id: number | null;
  agent_name: string | null;
}

export interface ProgressWebSocketMessage {
  type: 'progress_update' | 'connected' | 'error' | 'pong';
  iteration_id?: string;
  status?: IterationProgress;
  message?: string;
}

/**
 * Format elapsed time as human-readable duration.
 */
export function formatElapsedTime(seconds: number | null): string {
  if (!seconds || seconds < 0) return '';

  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);

  if (minutes < 60) {
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

// ─── File Processing Progress Types ────────────────────────────────

export interface TaskFileProcessingInfo {
  file_id: string;
  file_name: string;
  content_type: string | null;
  file_size: number | null;
  page_count: number | null;
  status: string;
  processing_level: string | null;
  has_ocr: boolean;
  has_ascii: boolean;
  has_page_previews: boolean;
  has_scout: boolean;
  has_embeddings: boolean;
  has_ocr_embeddings: boolean;
  has_ascii_embeddings: boolean;
  has_sense_summary: boolean;
  ocr_pages_done: number;
  ascii_pages_done: number;
  preview_pages_done: number;
}

export interface TaskFilesProcessingResponse {
  task_id: string;
  files: TaskFileProcessingInfo[];
  total_files: number;
  ready_files: number;
  all_ready: boolean;
}

export interface FileProgressWsMessage {
  type: 'file_progress';
  file_id: string;
  stage: string;        // 'ocr' | 'ascii' | 'preview'
  pages_done: number;
  total_pages: number;
}

/**
 * Get user-facing processing stage label from dp flags.
 * Content-type-aware, matching check_processing_completion.sql criteria.
 * When liveProgress is provided, shows per-page detail like "OCR 31/69".
 */
export function getFileProcessingStage(
  file: TaskFileProcessingInfo,
  liveProgress?: FileProgressWsMessage | null
): string {
  if (file.status === 'ready') return 'Ready';
  if (file.status === 'pending') return 'Queued';
  if (file.status === 'failed') return 'Failed';

  const pagesLabel = (stage: string, done: number, total: number): string =>
    total > 1 ? `${stage} ${done}/${total}` : stage;

  // Use live WS data if available
  if (liveProgress && liveProgress.total_pages > 0) {
    const stageMap: Record<string, string> = { ocr: 'OCR', ascii: 'Content Mining', preview: 'Page Previews' };
    const label = stageMap[liveProgress.stage] ?? liveProgress.stage;
    return pagesLabel(label, liveProgress.pages_done, liveProgress.total_pages);
  }

  // Fallback to REST data with page counts
  const pc = file.page_count ?? 0;

  // Processing — determine stage based on content type
  if (file.content_type === 'application/pdf') {
    if (!file.has_ocr) return pagesLabel('OCR', file.ocr_pages_done, pc);
    if (!file.has_scout) return 'Scouting';
    if (!file.has_ascii) return pagesLabel('Content Mining', file.ascii_pages_done, pc);
    if (!file.has_page_previews) return pagesLabel('Page Previews', file.preview_pages_done, pc);
    if (!file.has_ascii_embeddings) return 'Indexing';
    return 'Finalizing';
  }

  if (file.content_type?.startsWith('image/')) {
    if (!file.has_ocr) return 'OCR';
    if (!file.has_scout) return 'Scouting';
    if (!file.has_ocr_embeddings) return 'Indexing';
    return 'Finalizing';
  }

  // Text/Office/other
  if (!file.has_scout) return 'Scouting';
  if (!file.has_embeddings) return 'Indexing';
  return 'Finalizing';
}

/**
 * Get weighted processing percentage from dp flags.
 * Content-type-aware with weights matching completion criteria.
 * Uses page-level interpolation for smooth progress within stages.
 */
export function getFileProcessingPercent(
  file: TaskFileProcessingInfo,
  liveProgress?: FileProgressWsMessage | null
): number {
  if (file.status === 'ready') return 100;
  if (file.status === 'pending') return 0;
  if (file.status === 'failed') return 0;

  const pageRatio = (done: number, total: number): number =>
    total > 0 ? Math.min(done / total, 1) : 0;

  let percent = 0;

  if (file.content_type === 'application/pdf') {
    const pc = Math.max(file.page_count ?? 1, 1);

    // Use live WS data if it matches the current stage
    let ocrDone = file.ocr_pages_done;
    let asciiDone = file.ascii_pages_done;
    let previewDone = file.preview_pages_done;
    if (liveProgress) {
      if (liveProgress.stage === 'ocr') ocrDone = Math.max(ocrDone, liveProgress.pages_done);
      if (liveProgress.stage === 'ascii') asciiDone = Math.max(asciiDone, liveProgress.pages_done);
      if (liveProgress.stage === 'preview') previewDone = Math.max(previewDone, liveProgress.pages_done);
    }

    if (file.has_ocr) {
      percent += 15;
    } else {
      percent += 15 * pageRatio(ocrDone, pc);
    }
    if (file.has_scout) percent += 15;
    if (file.has_ascii) {
      percent += 25;
    } else if (file.has_ocr && file.has_scout) {
      percent += 25 * pageRatio(asciiDone, pc);
    }
    if (file.has_page_previews) {
      percent += 15;
    } else if (file.has_ocr) {
      percent += 15 * pageRatio(previewDone, pc);
    }
    if (file.has_ascii_embeddings) percent += 30;
  } else if (file.content_type?.startsWith('image/')) {
    if (file.has_ocr) percent += 30;
    if (file.has_scout) percent += 30;
    if (file.has_ocr_embeddings) percent += 40;
  } else {
    if (file.has_scout) percent += 40;
    if (file.has_embeddings) percent += 60;
  }

  return Math.round(percent);
}

/**
 * Estimate remaining seconds for file processing.
 * Content-type-aware with page-based scaling for PDFs.
 */
export function estimateFileEta(file: TaskFileProcessingInfo): number {
  if (file.status === 'ready' || file.status === 'failed') return 0;
  if (file.status === 'pending') return 60; // rough default

  const pages = file.page_count ?? 1;

  if (file.content_type === 'application/pdf') {
    // Parallel stages — take max of remaining
    const parallel: number[] = [];
    if (!file.has_ocr) parallel.push(3 * pages);
    if (!file.has_scout) parallel.push(Math.max(3 * pages, 5));
    if (!file.has_ascii) parallel.push(8 * pages);
    if (!file.has_page_previews) parallel.push(5 * pages);
    const parallelMax = parallel.length > 0 ? Math.max(...parallel) : 0;
    // Serial after parallel
    const serial = !file.has_ascii_embeddings ? 5 * pages : 0;
    return parallelMax + serial;
  }

  if (file.content_type?.startsWith('image/')) {
    let eta = 0;
    if (!file.has_ocr) eta += 5;
    if (!file.has_scout) eta += 10;
    if (!file.has_ocr_embeddings) eta += 10;
    return eta;
  }

  // Text/Office
  let eta = 0;
  if (!file.has_scout) eta += 5;
  if (!file.has_embeddings) eta += 5;
  return eta;
}
